var m = require('mithril');
var key = require('../../libs/keybindings');
var Model = require('../../libs/Model');
var modal = require('../../layout/components/modal');
var loaders = require('../../layout/components/loaders');
var Point = require('../models/point');
var pointTd = require('./pointTd');
var writeValueControl = require('./writeValueControl');
var canWriteColumn = require('../canWriteColumn');
var writeCommandEvents = require('../writeCommandEvents');
var writeOperations = require('../writeOperation');
var pointSubscriber = require('../../components/pointSubscriberClass');

var component = {};

component.errors = [];
component.successes = [];
component.rows = [];
component.title = 'Mass Override';
component.parentRid = null;
component.loading = false;
component.saving = false;
component.baselineRow = null;
component.baselinePointRecord = null;
component.baselineSignature = null;
component.formValue = null;
component.overrideMode = true;
component.increment = 1;
component.loadToken = 0;
component.saveToken = 0;
component.relinquishMode = 'none';
component.relinquishDuration = 1;
component.relinquishDurationUnit = 'hours';
component.relinquishAt = '';
component.releaseOnly = false;
component.submitCallback = null;

function normalizeRid(rid) {
  if (rid && typeof rid === 'object') {
    rid = rid['@rid'];
  }
  if (typeof rid !== 'string' || !rid) {
    return null;
  }
  return rid[0] === '#' ? rid.slice(1) : rid;
}

function ridForUrl(rid) {
  var normalized = normalizeRid(rid);
  return normalized ? normalized : null;
}

function ridWithHash(rid) {
  var normalized = normalizeRid(rid);
  return normalized ? '#' + normalized : null;
}

function pad2(value) {
  return String(value).padStart(2, '0');
}

function datetimeLocalFromTimestamp(timestamp) {
  var ts = Number(timestamp);
  if (!Number.isFinite(ts) || ts <= 0) return '';
  var date = new Date(ts);
  return [
    date.getFullYear(),
    '-',
    pad2(date.getMonth() + 1),
    '-',
    pad2(date.getDate()),
    'T',
    pad2(date.getHours()),
    ':',
    pad2(date.getMinutes())
  ].join('');
}

function timestampFromDatetimeLocal(value) {
  if (!value) return null;
  var ts = new Date(value).getTime();
  return Number.isFinite(ts) ? ts : null;
}

function stableJson(value) {
  if (!value || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) {
    return '[' + value.map(stableJson).join(',') + ']';
  }
  return '{' + Object.keys(value).sort().map(function (key) {
    return JSON.stringify(key) + ':' + stableJson(value[key]);
  }).join(',') + '}';
}

function normalizeNumber(value, fallback) {
  var numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
}

function isEnabled(value) {
  return value === true || value === 1 || value === '1';
}

function isManualMode(value) {
  return value === true || value === 1 || value === '1' || value === 'true' || Number(value) === 1;
}

function booleanNumber(value) {
  return isManualMode(value) ? 1 : 0;
}

function pointSupportsOverride(pointRecord) {
  return !!(pointRecord && isEnabled(pointRecord.overrides));
}

function displayPath(row) {
  if (row && row.path) return row.path;
  var point = row && row.columnRecord && row.columnRecord.in;
  return (point && (point.name || point['@rid'])) || 'Point';
}

function isDirectBacnetOverride(pointRecord) {
  var resource = pointRecord && pointRecord.resource;
  var resourceClass = resource && resource['@class'];
  var propertyId = Number(resource && resource.propertyId);
  var hasAnyLinkedTarget = pointRecord &&
    (pointRecord.overridemode && pointRecord.overridemode.in ||
      pointRecord.overridevalue && pointRecord.overridevalue.in);
  return resourceClass === 'BacnetProperty' &&
    (propertyId === 85 || propertyId === 87) &&
    !hasAnyLinkedTarget;
}

function controlPointRecordForRow(row) {
  if (!row || !row.overrideValuePointRecord) return null;
  if (row.sourcePointRecord && row.sourcePointRecord.unit === 'bool') {
    return Object.assign({}, row.overrideValuePointRecord, {
      unit: 'bool',
      enumeration: null
    });
  }
  return row.overrideValuePointRecord;
}

function overrideSignature(row) {
  var pointRecord = controlPointRecordForRow(row) || {};
  return [
    row && row.overrideStrategy || '',
    pointRecord.unit || '',
    pointRecord.destUnit || '',
    pointRecord.displayUnit || '',
    normalizeNumber(pointRecord.scale, 1),
    normalizeNumber(pointRecord.offset, 0),
    pointRecord.unit === 'bool' ? '' : stableJson(pointRecord.enumeration)
  ].join('|');
}

function rowHasOverrideTargets(row) {
  return !!(row && row.sourcePointRecord && row.overrideModePointRecord && row.overrideValuePointRecord && row.overrideStrategy);
}

function rowCanBeBaseline(row) {
  return rowHasOverrideTargets(row) &&
    pointSupportsOverride(row.sourcePointRecord) &&
    // The active-overrides view has no dashboard Column permission edge. Its
    // release-only writes rely on the API's fresh actor/topology authorization.
    (component.releaseOnly || canWriteColumn(row.columnRecord));
}

function configMismatchReason(row) {
  if (!row || !row.sourcePointRecord || !row.sourcePointRecord['@rid']) {
    return 'Point configuration could not be loaded';
  }
  if (!rowHasOverrideTargets(row)) {
    return row.disabledReason || 'Override mode/value points are not configured';
  }
  // Releasing does not share a value or conversion between rows. Each point's
  // current configuration is loaded and signed independently by its write.
  if (component.releaseOnly) {
    return null;
  }
  if (!component.baselineSignature) {
    return 'Baseline override configuration is still loading';
  }
  if (overrideSignature(row) !== component.baselineSignature) {
    return 'Override configuration differs from the first eligible point in this column';
  }
  return null;
}

function initializeFormValue(row) {
  var valuePointRecord = row && row.overrideValuePointRecord;
  var sourcePointRecord = row && row.sourcePointRecord;
  var controlPointRecord = controlPointRecordForRow(row);

  if (!valuePointRecord || !valuePointRecord['@rid'] || !controlPointRecord) {
    component.formValue = null;
    component.overrideMode = true;
    component.increment = 1;
    component.baselinePointRecord = null;
    return;
  }

  component.baselinePointRecord = controlPointRecord;
  component.overrideMode = isManualMode(sourcePointRecord && sourcePointRecord.overrideActive);

  if (sourcePointRecord && sourcePointRecord.unit === 'bool') {
    component.formValue = Boolean(Number(valuePointRecord.value));
    component.increment = 1;
  } else if (valuePointRecord.enumeration) {
    component.formValue = valuePointRecord.value;
    component.increment = 1;
  } else {
    component.formValue = Point.readValue({
      value: parseFloat(valuePointRecord.value) || 0,
      unit: valuePointRecord.unit || (sourcePointRecord && sourcePointRecord.unit),
      destUnit: valuePointRecord.destUnit || (sourcePointRecord && sourcePointRecord.destUnit),
      displayUnit: valuePointRecord.displayUnit || (sourcePointRecord && sourcePointRecord.displayUnit),
      scale: valuePointRecord.scale,
      offset: valuePointRecord.offset
    });
    component.increment = Number(valuePointRecord.increment) || Number(sourcePointRecord && sourcePointRecord.increment) || 1;
  }
}

function overrideValueForRow(row, formValue) {
  var valuePointRecord = row && row.overrideValuePointRecord;
  var sourcePointRecord = row && row.sourcePointRecord;
  var value = arguments.length > 1 ? formValue : component.formValue;

  if (value === undefined || value === null || value === '') {
    return null;
  }

  if (sourcePointRecord && sourcePointRecord.unit === 'bool') {
    return row.overrideStrategy === 'direct'
      ? Boolean(booleanNumber(value))
      : booleanNumber(value);
  }

  return Point.writeValue({
    scale: valuePointRecord && valuePointRecord.scale,
    unit: valuePointRecord && valuePointRecord.unit,
    destUnit: valuePointRecord && valuePointRecord.destUnit,
    displayUnit: valuePointRecord && valuePointRecord.displayUnit,
    offset: valuePointRecord && valuePointRecord.offset
  }, value);
}

function confirmedSourceValueForRow(row, operation, commandValue) {
  if (row.overrideStrategy === 'direct') return commandValue;
  var sourcePointRecord = row.sourcePointRecord;
  if (!sourcePointRecord) return commandValue;
  if (sourcePointRecord.unit === 'bool') {
    return booleanNumber(operation.formValue);
  }
  return Point.writeValue({
    scale: sourcePointRecord.scale,
    unit: sourcePointRecord.unit,
    destUnit: sourcePointRecord.destUnit,
    displayUnit: sourcePointRecord.displayUnit,
    offset: sourcePointRecord.offset
  }, operation.formValue);
}

function displayOverrideValue(row) {
  var pointRecord = controlPointRecordForRow(row) || component.baselinePointRecord;
  if (!component.overrideMode) {
    return 'Release override';
  }
  if (!component.baselinePointRecord || component.formValue === null || typeof component.formValue === 'undefined') {
    return 'Loading';
  }
  if (pointRecord && pointRecord.unit === 'bool') {
    return component.formValue ? 'Manual: On' : 'Manual: Off';
  }
  if (pointRecord && pointRecord.enumeration && pointRecord.unit !== 'bool') {
    return 'Manual: ' + (pointRecord.enumeration[component.formValue] || component.formValue);
  }
  var unit = Point.formatUnit(pointRecord || {});
  return 'Manual: ' + String(component.formValue) + (unit ? ' ' + unit : '');
}

function displayOverrideState(row) {
  return isManualMode(row && row.sourcePointRecord && row.sourcePointRecord.overrideActive) ? 'Manual' : 'Auto';
}

function redrawLoadingState() {
  if (m.redraw && typeof m.redraw.sync === 'function') {
    m.redraw.sync();
  } else {
    m.redraw();
  }
}

function waitForPaint() {
  return new Promise(function (resolve) {
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(function () { resolve(); });
    } else {
      setTimeout(resolve, 0);
    }
  });
}

function updateRowEligibility(row) {
  var disabledReason = row.disabledReason;
  if (!disabledReason && !rowHasOverrideTargets(row)) {
    disabledReason = configMismatchReason(row);
  }
  if (!disabledReason && component.releaseOnly &&
      !isManualMode(row.sourcePointRecord && row.sourcePointRecord.overrideActive)) {
    disabledReason = 'Point is no longer in override';
  }
  if (!disabledReason && !pointSupportsOverride(row.sourcePointRecord)) {
    disabledReason = 'Point is not configured for overrides';
  }
  if (!disabledReason && !component.releaseOnly && !canWriteColumn(row.columnRecord)) {
    disabledReason = 'You do not have write permission for this point';
  }
  if (!disabledReason) {
    disabledReason = configMismatchReason(row);
  }
  row.disabledReason = disabledReason;
  row.disabled = !!disabledReason;
  row.selected = !row.disabled;
}

function requestPoint(rid) {
  return loaders.requestWithLoader({
    loader: 'background',
    url: '/api/v1/point/' + ridForUrl(rid),
    method: 'GET'
  });
}

function loadOverrideTargets(row) {
  var sourcePointRid = ridForUrl(row && row.columnRecord && row.columnRecord.in && row.columnRecord.in['@rid']);
  if (!sourcePointRid) {
    row.disabled = true;
    row.disabledReason = 'Point RID is missing';
    return Promise.resolve(row);
  }

  return requestPoint(sourcePointRid)
  .then(function (sourcePointRecord) {
    row.sourcePointRecord = sourcePointRecord;
    row.sourcePointRid = ridWithHash(sourcePointRecord && sourcePointRecord['@rid']);

    if (isDirectBacnetOverride(sourcePointRecord)) {
      row.overrideStrategy = 'direct';
      row.overrideModePointRecord = sourcePointRecord;
      row.overrideValuePointRecord = sourcePointRecord;
      row.overrideModePointRid = row.sourcePointRid;
      row.overrideValuePointRid = row.sourcePointRid;
      row.disabled = false;
      row.disabledReason = null;
      return row;
    }

    var sourceResource = sourcePointRecord && sourcePointRecord.resource;
    var hasAnyLinkedTarget = !!(
      sourcePointRecord &&
      (sourcePointRecord.overridemode && sourcePointRecord.overridemode.in ||
        sourcePointRecord.overridevalue && sourcePointRecord.overridevalue.in)
    );
    if (
      sourceResource &&
      sourceResource['@class'] === 'BacnetProperty' &&
      !hasAnyLinkedTarget
    ) {
      row.disabled = true;
      row.disabledReason = 'Direct override is supported only for BACnet Present Value (85) and Priority Array (87) properties';
      return row;
    }

    var modeRid = ridForUrl(sourcePointRecord && sourcePointRecord.overridemode && sourcePointRecord.overridemode.in);
    var valueRid = ridForUrl(sourcePointRecord && sourcePointRecord.overridevalue && sourcePointRecord.overridevalue.in);
    if (!modeRid || !valueRid) {
      row.disabled = true;
      row.disabledReason = 'Override mode/value points are not configured';
      return row;
    }

    row.overrideStrategy = 'linked';
    return Promise.all([
      requestPoint(modeRid),
      requestPoint(valueRid)
    ])
    .then(function (results) {
      row.overrideModePointRecord = results[0];
      row.overrideValuePointRecord = results[1];
      row.overrideModePointRid = ridWithHash(results[0] && results[0]['@rid']);
      row.overrideValuePointRid = ridWithHash(results[1] && results[1]['@rid']);
      row.disabled = false;
      row.disabledReason = null;
      return row;
    });
  })
  .catch(function (error) {
    row.disabled = true;
    row.disabledReason = (error && (error.error || error.message)) || 'Point override configuration could not be loaded';
    row.status = 'error';
    row.statusMessage = row.disabledReason;
    return row;
  });
}

function loadRows() {
  var token = ++component.loadToken;
  component.loading = true;
  return Promise.all(component.rows.map(loadOverrideTargets))
  .then(function () {
    if (token !== component.loadToken) return;
    component.baselineRow = component.rows.filter(rowCanBeBaseline)[0] || null;
    component.baselineSignature = component.baselineRow ? overrideSignature(component.baselineRow) : null;
    initializeFormValue(component.baselineRow);
    if (component.releaseOnly) component.overrideMode = false;
    component.rows.forEach(updateRowEligibility);
    component.loading = false;
    m.redraw();
  })
  .catch(function (error) {
    if (token !== component.loadToken) return;
    component.loading = false;
    component.errors = [(error && (error.error || error.message)) || 'Unable to load mass override points'];
    m.redraw();
  });
}

function enabledRows() {
  return component.rows.filter(function (row) {
    return !row.disabled;
  });
}

function selectedRows() {
  return enabledRows().filter(function (row) {
    return row.selected;
  });
}

function allEnabledRowsSelected() {
  var rows = enabledRows();
  return rows.length > 0 && rows.every(function (row) {
    return !!row.selected;
  });
}

function someEnabledRowsSelected() {
  return component.rows.filter(function (row) {
    return row.selected && !row.disabled;
  }).length > 0;
}

function toggleAllRows() {
  var checked = !allEnabledRowsSelected();
  enabledRows().forEach(function (row) {
    row.selected = checked;
  });
}

function selectionButton(options) {
  var checked = !!options.checked;
  var mixed = !!options.mixed;
  var disabled = !!options.disabled;
  var className = 'ba bw1 br1 flex items-center justify-center center';
  if (options.class) className += ' ' + options.class;

  return m('button', {
    type: 'button',
    class: className,
    style: {
      width: '1.35rem',
      height: '1.35rem',
      minWidth: '1.35rem',
      padding: 0,
      borderColor: checked || mixed ? '#58abe2' : '#777',
      backgroundColor: checked || mixed ? '#58abe2' : 'transparent',
      color: checked || mixed ? '#111' : '#f4f4f4',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      lineHeight: 1
    },
    disabled: disabled,
    title: options.label,
    'aria-label': options.label,
    'aria-checked': mixed ? 'mixed' : checked ? 'true' : 'false',
    role: 'checkbox',
    'data-select-all-children': options.selectAll ? true : null,
    onclick: function (evt) {
      evt.preventDefault();
      evt.stopPropagation();
      if (!disabled && typeof options.onclick === 'function') {
        options.onclick();
      }
    },
    onkeydown: function (evt) {
      if (evt.key !== 'Enter' && evt.key !== ' ') return;
      evt.preventDefault();
      evt.stopPropagation();
      if (!disabled && typeof options.onclick === 'function') {
        options.onclick();
      }
    }
  }, [
    checked ? m('i.f4.ion-checkmark') : mixed ? m('span.b.f4', '-') : null
  ]);
}

function selectAllCheckbox() {
  var enabledCount = enabledRows().length;
  var checked = allEnabledRowsSelected();
  var disabled = component.loading || component.saving || enabledCount === 0;
  var label = enabledCount === 0
    ? 'No override points to select'
    : checked ? 'Deselect all override points' : 'Select all override points';

  return selectionButton({
    checked: checked,
    mixed: !checked && someEnabledRowsSelected(),
    disabled: disabled,
    label: label,
    class: 'mass-override-select-all-checkbox',
    selectAll: true,
    onclick: toggleAllRows
  });
}

function rowCheckbox(row) {
  var checked = !!row.selected;
  var disabled = !!row.disabled || component.saving;
  var label = row.disabledReason || (checked ? 'Exclude point from override' : 'Include point in override');
  return selectionButton({
    checked: checked,
    disabled: disabled,
    label: label,
    class: 'mass-override-row-checkbox',
    onclick: function () {
      row.selected = !row.selected;
    }
  });
}

function statusCell(row) {
  if (row.status === 'loading') {
    return m('span.inline-flex.items-center.justify-center', [
      m('i.f3.blue.ion-load-a.spin', { title: 'Sending override' })
    ]);
  }
  if (row.status === 'success') {
    return m('i.f3.green.ion-checkmark', { title: 'Override succeeded' });
  }
  if (row.status === 'error') {
    return m('i.f3.red.ion-close-round', { title: row.statusMessage || 'Override failed' });
  }
  if (row.status === 'warning') {
    return m('i.f3.gold.ion-alert-circled', { title: row.statusMessage || 'Override applied with a warning' });
  }
  if (row.disabledReason) {
    return m('span.f6.moon-gray', { title: row.disabledReason }, 'Disabled');
  }
  return '';
}

function validateSelectedRows(rowsToOverride) {
  if (!component.releaseOnly && !component.baselineSignature) {
    return 'Override configuration is still loading.';
  }
  for (var idx = 0; idx < rowsToOverride.length; idx += 1) {
    var row = rowsToOverride[idx];
    var disabledReason = row.disabledReason || (component.releaseOnly ? null : configMismatchReason(row));
    if (disabledReason) {
      return displayPath(row) + ': ' + disabledReason;
    }
  }
  return null;
}

function validateOverrideValue(operation) {
  if (!operation.overrideMode) return null;
  var pointRecord = operation.baselinePointRecord || {};
  var value = operation.formValue;
  if (value === undefined || value === null || value === '') {
    return 'Enter an override value before sending.';
  }
  if (pointRecord.unit !== 'bool' && !pointRecord.enumeration && !Number.isFinite(Number(value))) {
    return 'Enter a valid numeric override value before sending.';
  }
  return null;
}

function buildRelinquishPlan(operation) {
  if (!operation.overrideMode || operation.relinquishMode === 'none') {
    return { action: 'cancel', mode: 'none' };
  }

  if (operation.relinquishMode === 'duration') {
    var amount = Number(operation.relinquishDuration);
    if (!Number.isFinite(amount) || amount <= 0) {
      throw new Error('Relinquish duration must be greater than zero.');
    }
    if (['minutes', 'hours'].indexOf(operation.relinquishDurationUnit) === -1) {
      throw new Error('Relinquish duration unit is invalid.');
    }
    var multiplier = operation.relinquishDurationUnit === 'minutes' ? 60 * 1000 : 60 * 60 * 1000;
    return {
      action: 'schedule',
      mode: 'duration',
      durationMs: Math.round(amount * multiplier)
    };
  }

  if (operation.relinquishMode === 'at') {
    var releaseAt = timestampFromDatetimeLocal(operation.relinquishAt);
    if (!releaseAt) {
      throw new Error('Relinquish time is required.');
    }
    return {
      action: 'schedule',
      mode: 'at',
      releaseAt: releaseAt
    };
  }

  throw new Error('Relinquish mode is invalid.');
}

function releaseAtForPlan(plan) {
  if (!plan || plan.action !== 'schedule') return null;
  if (Number.isFinite(Number(plan.releaseAt))) return Number(plan.releaseAt);
  plan.releaseAt = Date.now() + plan.durationMs;
  return plan.releaseAt;
}

function relinquishDeadlineExpired(operation) {
  return !!(operation && operation.relinquishPlan &&
    operation.relinquishPlan.action === 'schedule' &&
    Number.isFinite(Number(operation.relinquishPlan.releaseAt)) &&
    Number(operation.relinquishPlan.releaseAt) <= Date.now());
}

function expiredPhaseError(prepared) {
  var error = new Error(
    'Scheduled relinquish deadline elapsed before the next override phase could start.'
  );
  error.code = 'MASS_OVERRIDE_RELINQUISH_EXPIRED';
  error.batchRowExpired = true;
  error.expiringWriteOperation = prepared;
  return error;
}

function operationIsCurrent(operation) {
  return !operation.token || operation.token === component.saveToken;
}

function assertOperationCurrent(operation) {
  if (!operationIsCurrent(operation)) {
    throw new Error('Mass override operation was canceled.');
  }
}

function buildOverrideWriteSpec(
  pointRecord,
  sourcePointRecord,
  body,
  operation,
  relinquishScheduleRequest,
  operationKey
) {
  var pointRid = pointRecord && pointRecord['@rid'];
  var requestBody = Object.assign({}, body, {
    expectedPointConfiguration: Point.expectedPointConfiguration([
      sourcePointRecord,
      pointRecord
    ])
  });
  if (relinquishScheduleRequest) {
    requestBody.relinquishSchedule = relinquishScheduleRequest;
  }
  var url = '/api/v1/point/' + ridForUrl(pointRid) + '/write/' + operation.parentRid;
  var rowKey = ridWithHash(body && body.sourcePointRid || operation.rowScope);
  var writeOperationKey = rowKey + ':' + operationKey;
  var isInitialRelinquishRequest = relinquishScheduleRequest ===
    operation.relinquishScheduleRequest;
  return {
    key: writeOperationKey,
    rowKey: rowKey,
    phase: operationKey,
    pointRid: pointRid,
    url: url,
    body: requestBody,
    scope: [operation.parentRid, writeOperationKey].join(':'),
    intent: {
      request: Object.assign({}, requestBody, isInitialRelinquishRequest
        ? { relinquishSchedule: null }
        : {}),
      relinquish: isInitialRelinquishRequest
        ? operation.relinquishIntent
        : relinquishScheduleRequest || null
    },
    rowRequestIdentity: operation.rowRequestIdentities &&
      operation.rowRequestIdentities[rowKey]
  };
}

async function requestOverrideWrite(
  pointRecord,
  sourcePointRecord,
  body,
  operation,
  relinquishScheduleRequest,
  operationKey
) {
  var spec = buildOverrideWriteSpec(
    pointRecord,
    sourcePointRecord,
    body,
    operation,
    relinquishScheduleRequest,
    operationKey
  );
  operation.preparedWriteOperations = operation.preparedWriteOperations || {};
  var prepared = operation.preparedWriteOperations[spec.key];
  if (!prepared) {
    prepared = (await writeOperations.seedBatchOperations(operation.batch, [spec]))[0];
    operation.preparedWriteOperations[spec.key] = prepared;
  }
  if (relinquishDeadlineExpired(operation) && prepared.attemptedAt === null) {
    // Marking attempted is the transport boundary. An old attempted phase may
    // replay after the deadline, but a new/unattempted phase must not cross it.
    throw expiredPhaseError({ key: spec.key, operation: prepared });
  }
  operation.activeWriteOperation = {
    key: spec.key,
    operation: prepared
  };
  return writeCommandEvents.waitForPointWrite({
    pointRid: writeOperations.pointRidForOperation(prepared),
    request: async function () {
      assertOperationCurrent(operation);
      await writeOperations.markAttempted(prepared);
      return loaders.requestWithLoader({
        loader: 'background',
        url: prepared.url,
        method: 'PUT',
        body: prepared.body
      });
    },
    timeoutMessage: 'Timed out waiting for override confirmation.'
  }).then(function (confirmation) {
    operation.activeWriteOperation = null;
    operation.usedWriteOperations.push({
      key: spec.key,
      operation: prepared
    });
    return confirmation;
  });
}

function updateSourceOverrideActive(row, active, parentRid, commandRid) {
  return loaders.requestWithLoader({
    loader: 'background',
    url: '/api/v1/point/' + ridForUrl(row.sourcePointRid) + '/runtime/' + ridForUrl(parentRid),
    method: 'PUT',
    body: {
      sourcePointRid: ridWithHash(row.sourcePointRid),
      overrideActive: active,
      commandRid: commandRid
    }
  });
}

function errorMessage(error) {
  return (error && (error.error || error.message)) || String(error || 'Unknown error');
}

function overrideRowKey(row) {
  return ridWithHash(
    row && (row.sourcePointRid ||
      row.sourcePointRecord && row.sourcePointRecord['@rid'] ||
      row.columnRecord && row.columnRecord.in && row.columnRecord.in['@rid'])
  );
}

function overrideRowRequestIdentity(row, operation) {
  var active = operation.overrideMode ? 1 : 0;
  var valueData = operation.overrideMode
    ? overrideValueForRow(row, operation.formValue)
    : row.overrideValuePointRecord && row.overrideValuePointRecord.value;
  var targets;
  if (row.overrideStrategy === 'direct') {
    targets = [{ pointRecord: row.overrideValuePointRecord, value: active ? valueData : null }];
  } else if (active) {
    targets = [
      { pointRecord: row.overrideValuePointRecord, value: valueData },
      { pointRecord: row.overrideModePointRecord, value: active }
    ];
  } else {
    targets = [
      { pointRecord: row.overrideModePointRecord, value: active },
      { pointRecord: row.overrideValuePointRecord, value: valueData }
    ];
  }
  return {
    strategy: row.overrideStrategy,
    targets: targets.map(function (target) {
      return {
        pointRid: target.pointRecord && target.pointRecord['@rid'],
        value: target.value,
        expectedPointConfiguration: Point.expectedPointConfiguration([
          row.sourcePointRecord,
          target.pointRecord
        ])
      };
    })
  };
}

function initialOverrideWriteSpecForRow(row, operation) {
  var active = operation.overrideMode ? 1 : 0;
  var sourcePointRid = ridWithHash(
    row.sourcePointRid ||
    row.sourcePointRecord && row.sourcePointRecord['@rid'] ||
    row.columnRecord && row.columnRecord.in && row.columnRecord.in['@rid']
  );
  var valueData = operation.overrideMode
    ? overrideValueForRow(row, operation.formValue)
    : row.overrideValuePointRecord && row.overrideValuePointRecord.value;
  if (row.overrideStrategy === 'direct') {
    return buildOverrideWriteSpec(
      row.overrideValuePointRecord,
      row.sourcePointRecord,
      {
        value: active ? valueData : null,
        commandType: 'override',
        active: active,
        sourcePointRid: sourcePointRid
      },
      operation,
      operation.relinquishScheduleRequest,
      'direct'
    );
  }
  if (active) {
    return buildOverrideWriteSpec(
      row.overrideValuePointRecord,
      row.sourcePointRecord,
      {
        value: valueData,
        commandType: 'override',
        active: active,
        sourcePointRid: sourcePointRid
      },
      operation,
      operation.relinquishScheduleRequest,
      'manual-value'
    );
  }
  return buildOverrideWriteSpec(
    row.overrideModePointRecord,
    row.sourcePointRecord,
    {
      value: active,
      commandType: 'override',
      active: active,
      sourcePointRid: sourcePointRid
    },
    operation,
    operation.relinquishScheduleRequest,
    'auto-mode'
  );
}

async function writeOverrideRow(row) {
  var operation = arguments.length > 1 ? arguments[1] : {
    parentRid: component.parentRid,
    overrideMode: component.overrideMode,
    formValue: component.formValue,
    relinquishPlan: buildRelinquishPlan({
      overrideMode: component.overrideMode,
      relinquishMode: component.relinquishMode,
      relinquishDuration: component.relinquishDuration,
      relinquishDurationUnit: component.relinquishDurationUnit,
      relinquishAt: component.relinquishAt
    })
  };
  var active = operation.overrideMode ? 1 : 0;
  var sourcePointRid = ridWithHash(
    row.sourcePointRid ||
    row.sourcePointRecord && row.sourcePointRecord['@rid'] ||
    row.columnRecord && row.columnRecord.in && row.columnRecord.in['@rid']
  );
  operation.rowScope = sourcePointRid;
  operation.activeWriteOperation = null;
  operation.usedWriteOperations = [];
  var valueData = operation.overrideMode
    ? overrideValueForRow(row, operation.formValue)
    : row.overrideValuePointRecord && row.overrideValuePointRecord.value;
  var relinquishScheduleRequest = Object.prototype.hasOwnProperty.call(
    operation,
    'relinquishScheduleRequest'
  ) ? operation.relinquishScheduleRequest : null;
  var bundledSchedulePersisted = false;
  var bundledCancellationHandled = false;
  var relinquishScheduleWarnings = [];
  var finalConfirmation;

  function recordBundledScheduleResult(confirmation) {
    var command = confirmation && confirmation.command;
    if (command && command.relinquishSchedule) {
      bundledSchedulePersisted = true;
      var persistedReleaseAt = Number(command.relinquishSchedule.releaseAt);
      if (Number.isFinite(persistedReleaseAt) && persistedReleaseAt > 0 &&
          operation.relinquishPlan && operation.relinquishPlan.action === 'schedule') {
        operation.relinquishPlan.releaseAt = Math.min(
          Number(operation.relinquishPlan.releaseAt),
          persistedReleaseAt
        );
      }
    }
    if (command && command.relinquishScheduleWarning) {
      var warning = String(command.relinquishScheduleWarning);
      warning = warning.charAt(0).toLowerCase() + warning.slice(1);
      if (relinquishScheduleWarnings.indexOf(warning) === -1) {
        relinquishScheduleWarnings.push(warning);
      }
    }
    if (command && command.relinquishScheduleCancellation && command.relinquishScheduleCancellation.requested) {
      bundledCancellationHandled = true;
    }
  }

  function continuationFor(confirmation) {
    var command = confirmation && confirmation.command;
    var generation = command && command.relinquishSchedule && command.relinquishSchedule.generation ||
      command && command.relinquishScheduleCancellation && command.relinquishScheduleCancellation.generation;
    var commandRid = command && command.commandRid;
    if (typeof generation !== 'string' || !generation || !commandRid) {
      throw new Error('The server did not return a confirmed override continuation token.');
    }
    return {
      action: 'continue',
      generation: generation,
      commandRid: commandRid
    };
  }

  if (!Object.prototype.hasOwnProperty.call(operation, 'relinquishScheduleRequest') &&
      operation.relinquishPlan.action === 'schedule') {
    var releaseAt = releaseAtForPlan(operation.relinquishPlan);
    if (!releaseAt) {
      throw new Error('Scheduled relinquish time is invalid.');
    }
    // The batch freezes one absolute timestamp before any row starts. The same
    // request that queues this row's first Manual write persists that deadline.
    relinquishScheduleRequest = { releaseAt: releaseAt };
  } else if (!Object.prototype.hasOwnProperty.call(operation, 'relinquishScheduleRequest') &&
      operation.relinquishPlan.action === 'cancel') {
    relinquishScheduleRequest = { action: 'cancel' };
  }
  operation.relinquishScheduleRequest = relinquishScheduleRequest;

  assertOperationCurrent(operation);
  if (row.overrideStrategy === 'direct') {
    finalConfirmation = await requestOverrideWrite(
      row.overrideValuePointRecord,
      row.sourcePointRecord,
      {
      value: active ? valueData : null,
      commandType: 'override',
      active: active,
      sourcePointRid: sourcePointRid
      },
      operation,
      relinquishScheduleRequest,
      'direct'
    );
    recordBundledScheduleResult(finalConfirmation);
  } else if (active) {
    var valueConfirmation = await requestOverrideWrite(
      row.overrideValuePointRecord,
      row.sourcePointRecord,
      {
      value: valueData,
      commandType: 'override',
      active: active,
      sourcePointRid: sourcePointRid
      },
      operation,
      relinquishScheduleRequest,
      'manual-value'
    );
    recordBundledScheduleResult(valueConfirmation);
    assertOperationCurrent(operation);
    finalConfirmation = await requestOverrideWrite(
      row.overrideModePointRecord,
      row.sourcePointRecord,
      {
      value: active,
      commandType: 'override',
      active: active,
      sourcePointRid: sourcePointRid
      },
      operation,
      continuationFor(valueConfirmation),
      'manual-mode'
    );
    recordBundledScheduleResult(finalConfirmation);
  } else {
    var modeConfirmation = await requestOverrideWrite(
      row.overrideModePointRecord,
      row.sourcePointRecord,
      {
      value: active,
      commandType: 'override',
      active: active,
      sourcePointRid: sourcePointRid
      },
      operation,
      relinquishScheduleRequest,
      'auto-mode'
    );
    recordBundledScheduleResult(modeConfirmation);
    assertOperationCurrent(operation);
    finalConfirmation = await requestOverrideWrite(
      row.overrideValuePointRecord,
      row.sourcePointRecord,
      {
      value: valueData,
      commandType: 'override',
      active: active,
      sourcePointRid: sourcePointRid
      },
      operation,
      continuationFor(modeConfirmation),
      'auto-value'
    );
    recordBundledScheduleResult(finalConfirmation);
  }

  assertOperationCurrent(operation);
  operation.activeWriteOperation = null;
  var partialErrors = relinquishScheduleWarnings.slice();
  if (relinquishScheduleRequest && relinquishScheduleRequest.action === 'cancel' && !bundledCancellationHandled) {
    partialErrors.push('the server did not confirm the bundled relinquish cancellation');
  } else if (relinquishScheduleRequest && relinquishScheduleRequest.action !== 'cancel' && !bundledSchedulePersisted) {
    partialErrors.push('the server did not return the persisted relinquish schedule');
  }
  try {
    var finalCommand = finalConfirmation && finalConfirmation.command || {};
    await updateSourceOverrideActive(row, active, operation.parentRid, finalCommand.commandRid);
    if (row.sourcePointRecord) row.sourcePointRecord.overrideActive = active;
    if (row.columnRecord && row.columnRecord.in) row.columnRecord.in.overrideActive = active;
    if (active === 1) {
      var confirmedValue = confirmedSourceValueForRow(row, operation, valueData);
      if (row.sourcePointRecord) row.sourcePointRecord.value = confirmedValue;
      if (row.columnRecord && row.columnRecord.in) row.columnRecord.in.value = confirmedValue;
      pointSubscriber.publishConfirmedOverrideUpdate({
        rid: sourcePointRid,
        value: confirmedValue,
        overrideActive: active,
        overrideRuntimeCommandRid: finalCommand.commandRid
      });
    }
  } catch (stateError) {
    partialErrors.push('point state could not be updated: ' + errorMessage(stateError));
  }
  assertOperationCurrent(operation);

  if (partialErrors.length) {
    var partialError = new Error('Override applied, but ' + partialErrors.join('; '));
    partialError.overrideApplied = true;
    throw partialError;
  }
}

function modeButton(label, manualValue) {
  var active = component.overrideMode === manualValue;
  var disabled = component.loading || component.saving;
  return m('button', {
    type: 'button',
    class: 'ba bw1 br1 tc pointer mass-override-mode-button',
    style: {
      width: '6rem',
      maxWidth: '6rem',
      minWidth: '6rem',
      height: '3.25rem',
      minHeight: '3.25rem',
      maxHeight: '3.25rem',
      marginRight: manualValue ? 0 : '0.5rem',
      backgroundColor: active && manualValue ? '#f5c84b' : active ? '#777' : 'transparent',
      color: active && manualValue ? '#000' : '#fff',
      fontSize: '1.2em',
      lineHeight: '3.25rem',
      opacity: disabled ? 0.5 : 1,
      cursor: disabled ? 'not-allowed' : 'pointer'
    },
    disabled: disabled,
    onclick: function (evt) {
      evt.preventDefault();
      evt.stopPropagation();
      if (disabled) return;
      component.overrideMode = manualValue;
    }
  }, label);
}

function modeControls() {
  if (component.releaseOnly) {
    return m('div.mb3', [
      m('div.f2', 'Release to Auto'),
      m('div.f5.moon-gray.mt1', 'The selected points will be released and any scheduled releases will be canceled.')
    ]);
  }
  return m('div.mb3', [
    m('div.flex.items-center', [
      m('span.f2.mr2', 'Mode'),
      m('div.flex', [
        modeButton('Auto', false),
        modeButton('Manual', true)
      ])
    ])
  ]);
}

function valueControls() {
  if (!component.overrideMode) {
    return m('div.f5.moon-gray.mb3', 'Selected rows will be released to Auto and any scheduled releases will be canceled.');
  }
  if (!component.baselinePointRecord) {
    return m('div.tc.pa2', 'Loading override form...');
  }
  return m('div.mb3', [
    m(writeValueControl, {
      pointRecord: component.baselinePointRecord,
      value: component.formValue,
      increment: component.increment,
      onchange: function (value) {
        component.formValue = value;
        component.overrideMode = true;
      }
    })
  ]);
}

function setRelinquishMode(mode) {
  component.relinquishMode = mode;
  if (mode === 'duration' && !Number(component.relinquishDuration)) {
    component.relinquishDuration = 1;
  }
  if (mode === 'at' && !component.relinquishAt) {
    component.relinquishAt = datetimeLocalFromTimestamp(Date.now() + (60 * 60 * 1000));
  }
}

function relinquishModeButton(mode, label) {
  var selected = component.relinquishMode === mode;
  var disabled = component.loading || component.saving;
  return m('button', {
    type: 'button',
    class: 'ba bw1 br1 tc pointer mass-override-relinquish-mode-button',
    style: {
      width: '6rem',
      minWidth: '6rem',
      height: '2.75rem',
      marginRight: mode === 'at' ? 0 : '0.5rem',
      backgroundColor: selected ? '#777' : 'transparent',
      color: '#fff',
      opacity: disabled ? 0.5 : 1,
      cursor: disabled ? 'not-allowed' : 'pointer'
    },
    disabled: disabled,
    'aria-pressed': selected ? 'true' : 'false',
    onclick: function (evt) {
      evt.preventDefault();
      evt.stopPropagation();
      if (!disabled) setRelinquishMode(mode);
    }
  }, label);
}

function relinquishControls() {
  if (!component.overrideMode) return null;
  var disabled = component.loading || component.saving;
  var inputStyle = {
    height: '2.75rem',
    minHeight: '2.75rem',
    boxSizing: 'border-box',
    fontSize: '1rem'
  };

  return m('div.mb3.pt3.bt.b--mid-gray', { 'data-testid': 'mass-override-relinquish-section' }, [
    m('div.flex.items-center.mb2', [
      m('span.f2.mr2', 'Release'),
      m('div.flex', [
        relinquishModeButton('none', 'None'),
        relinquishModeButton('duration', 'For'),
        relinquishModeButton('at', 'Until')
      ])
    ]),
    component.relinquishMode === 'duration'
      ? m('div.flex.items-center', [
        m('span.f4.mr2', 'After'),
        m('input.mass-override-relinquish-duration', {
          type: 'number',
          min: 1,
          step: 1,
          disabled: disabled,
          value: component.relinquishDuration,
          oninput: function (evt) { component.relinquishDuration = evt.target.value; },
          onchange: function (evt) { component.relinquishDuration = evt.target.value; },
          style: Object.assign({}, inputStyle, {
            width: '6rem',
            marginRight: '0.5rem'
          })
        }),
        m('select.form-control.mass-override-relinquish-duration-unit', {
          disabled: disabled,
          value: component.relinquishDurationUnit,
          onchange: function (evt) { component.relinquishDurationUnit = evt.target.value; },
          style: Object.assign({}, inputStyle, { width: '9rem' })
        }, [
          m('option', { value: 'minutes' }, 'minutes'),
          m('option', { value: 'hours' }, 'hours')
        ])
      ])
      : null,
    component.relinquishMode === 'at'
      ? m('div.flex.items-center', [
        m('span.f4.mr2', 'At'),
        m('input.mass-override-relinquish-at', {
          type: 'datetime-local',
          disabled: disabled,
          value: component.relinquishAt,
          oninput: function (evt) { component.relinquishAt = evt.target.value; },
          onchange: function (evt) { component.relinquishAt = evt.target.value; },
          style: Object.assign({}, inputStyle, { width: '16rem' })
        })
      ])
      : null,
    component.relinquishMode === 'none'
      ? m('div.f6.moon-gray.mt2', 'No timed release will be kept for the selected rows.')
      : null
  ]);
}

component.editDialogue = function (options) {
  options = options || {};
  component.saveToken += 1;
  component.errors = [];
  component.successes = [];
  component.parentRid = normalizeRid(options.parentRid);
  component.title = options.title || 'Mass Override';
  component.rows = (options.rows || []).map(function (row, index) {
    return Object.assign({
      index: index,
      selected: false,
      disabled: true,
      disabledReason: 'Point override configuration is loading',
      status: null,
      statusMessage: null
    }, row);
  });
  component.loading = true;
  component.saving = false;
  component.baselineRow = null;
  component.baselinePointRecord = null;
  component.baselineSignature = null;
  component.formValue = null;
  component.overrideMode = true;
  component.increment = 1;
  component.relinquishMode = 'none';
  component.relinquishDuration = 1;
  component.relinquishDurationUnit = 'hours';
  component.relinquishAt = '';
  component.releaseOnly = options.releaseOnly === true;
  component.submitCallback = typeof options.submitCallback === 'function'
    ? options.submitCallback
    : null;
  if (component.releaseOnly) component.overrideMode = false;
  component.writeOperations = {};

  var submit = function () {
    return component.save();
  };

  key.setScope('modal');
  key('enter', 'modal', submit);

  modal.vm.title(component.title);
  modal.vm.body = component.view;
  modal.vm.buttons([
    {
      label: component.releaseOnly ? 'Release' : 'Send',
      class: 'btn-good',
      disabled: function () {
        return component.loading || component.saving || selectedRows().length === 0 ||
          (!component.releaseOnly && !component.baselineSignature);
      },
      func: submit
    }
  ]);
  modal.vm.open({
    extrawidth: true,
    suppressPointUpdates: component.rows.map(function (row) {
      return row && row.columnRecord && row.columnRecord.in && row.columnRecord.in['@rid'];
    })
  });

  return loadRows();
};

component.save = async function () {
  if (component.loading || component.saving) return false;
  if (!component.parentRid) {
    component.errors = ['Cannot override points without a parent location or equipment context.'];
    m.redraw();
    return false;
  }

  var rowsToOverride = selectedRows();
  if (rowsToOverride.length === 0) {
    component.errors = ['Select at least one point to override.'];
    m.redraw();
    return false;
  }

  var validationError = validateSelectedRows(rowsToOverride);
  if (validationError) {
    component.errors = [validationError];
    m.redraw();
    return false;
  }

  var operation = {
    parentRid: component.parentRid,
    overrideMode: component.overrideMode,
    formValue: component.formValue,
    baselinePointRecord: component.baselinePointRecord,
    relinquishMode: component.relinquishMode,
    relinquishDuration: component.relinquishDuration,
    relinquishDurationUnit: component.relinquishDurationUnit,
    relinquishAt: component.relinquishAt,
    writeOperations: component.writeOperations || {}
  };
  component.writeOperations = operation.writeOperations;
  var valueError = validateOverrideValue(operation);
  if (valueError) {
    component.errors = [valueError];
    m.redraw();
    return false;
  }
  try {
    operation.relinquishPlan = buildRelinquishPlan(operation);
    if (operation.relinquishPlan.action === 'schedule') {
      releaseAtForPlan(operation.relinquishPlan);
    }
  } catch (relinquishError) {
    component.errors = [errorMessage(relinquishError)];
    m.redraw();
    return false;
  }
  operation.relinquishIntent = {
    mode: operation.relinquishMode,
    duration: operation.relinquishMode === 'duration'
      ? Number(operation.relinquishDuration)
      : null,
    durationUnit: operation.relinquishMode === 'duration'
      ? operation.relinquishDurationUnit
      : null,
    at: operation.relinquishMode === 'at' ? operation.relinquishAt : null,
    active: operation.overrideMode ? 1 : 0
  };
  var selectedSourcePointRids = rowsToOverride.map(overrideRowKey).sort();
  var durableBatchIntent = {
    parentRid: operation.parentRid,
    overrideMode: operation.overrideMode,
    formValue: operation.overrideMode ? operation.formValue : null,
    relinquish: operation.relinquishIntent,
    selectedSourcePointRids: selectedSourcePointRids
  };
  var rowConflictScopes = {};
  var rowConflictGroups = {};
  var rowRequestIdentities = {};
  selectedSourcePointRids.forEach(function (rowKey) {
    rowConflictScopes[rowKey] = [
      writeOperations.pointConflictScope(rowKey, operation.parentRid)
    ];
    rowConflictGroups[rowKey] = stableJson({
      surface: 'mass-override',
      batchIntent: durableBatchIntent
    });
  });
  rowsToOverride.forEach(function (row) {
    rowRequestIdentities[overrideRowKey(row)] = overrideRowRequestIdentity(row, operation);
  });
  operation.rowRequestIdentities = rowRequestIdentities;
  operation.massIntent = stableJson(durableBatchIntent);
  rowsToOverride.forEach(function (row) {
    row.currentOverrideIntent = stableJson({
      mass: operation.massIntent,
      sourcePointRid: row.sourcePointRid,
      configuration: overrideSignature(row)
    });
  });
  component.errors = [];
  component.successes = [];
  component.saving = true;
  var saveToken = ++component.saveToken;
  operation.token = saveToken;
  rowsToOverride.forEach(function (row) {
    row.status = null;
    row.statusMessage = null;
  });
  m.redraw();

  try {
    operation.batch = await writeOperations.prepareBatch(
      operation.writeOperations,
      'mass-override-batch',
      {
        kind: 'mass-override',
        contextKey: operation.parentRid,
        rows: selectedSourcePointRids,
        intent: durableBatchIntent,
        rowConflictScopes: rowConflictScopes,
        rowConflictGroups: rowConflictGroups,
        rowRequestIdentities: rowRequestIdentities,
        releaseAt: operation.relinquishPlan.action === 'schedule'
          ? releaseAtForPlan(operation.relinquishPlan)
          : null,
        recoveryPolicy: writeOperations.ATTENDED_RECOVERY_POLICY
      }
    );
    if (saveToken !== component.saveToken) return false;

    if (operation.relinquishPlan.action === 'schedule') {
      if (!Number.isFinite(Number(operation.batch.releaseAt))) {
        throw new Error('The durable mass-override release deadline is missing.');
      }
      // A duration is converted to one absolute deadline before row one. A
      // restored partial batch always reuses that original deadline.
      operation.relinquishPlan.releaseAt = Number(operation.batch.releaseAt);
      operation.relinquishScheduleRequest = {
        releaseAt: operation.relinquishPlan.releaseAt
      };
    } else {
      operation.relinquishScheduleRequest = { action: 'cancel' };
    }

    operation.preparedWriteOperations = {};
    var pendingRows = [];
    rowsToOverride.forEach(function (row) {
      var rowKey = overrideRowKey(row);
      var durableRow = operation.batch.rows[rowKey];
      if (durableRow && (durableRow.status === 'success' || durableRow.status === 'expired')) {
        row.status = durableRow.warning ? 'warning' : 'success';
        row.statusMessage = durableRow.warning;
        row.successIntent = row.currentOverrideIntent;
      } else {
        pendingRows.push(row);
      }
    });

    var initialSpecs = pendingRows.map(function (row) {
      return initialOverrideWriteSpecForRow(row, operation);
    });
    var seededOperations = await writeOperations.seedBatchOperations(
      operation.batch,
      initialSpecs
    );
    if (saveToken !== component.saveToken) return false;
    seededOperations.forEach(function (prepared) {
      operation.preparedWriteOperations[
        prepared.rowKey + ':' + prepared.phase
      ] = prepared;
    });
    rowsToOverride = pendingRows;
  } catch (recoveryError) {
    if (saveToken !== component.saveToken) return false;
    component.errors = [errorMessage(recoveryError)];
    component.successes = [];
    component.saving = false;
    m.redraw();
    return false;
  }

  var successCount = 0;
  var failureCount = 0;
  var warningCount = 0;

  for (var idx = 0; idx < rowsToOverride.length; idx += 1) {
    if (saveToken !== component.saveToken) return false;
    var row = rowsToOverride[idx];
    row.status = 'loading';
    row.statusMessage = null;
    redrawLoadingState();
    await waitForPaint();
    if (saveToken !== component.saveToken) return false;

    try {
      await writeOverrideRow(row, operation);
      if (saveToken !== component.saveToken) return false;
      await writeOperations.markBatchRowSuccess(
        operation.writeOperations,
        'mass-override-batch',
        operation.batch,
        overrideRowKey(row)
      );
      row.status = 'success';
      row.successIntent = row.currentOverrideIntent;
      successCount += 1;
    } catch (error) {
      if (saveToken !== component.saveToken) return false;
      if (error && error.writeApplied) {
        row.status = 'success';
        row.successIntent = row.currentOverrideIntent;
        successCount += 1;
        m.redraw();
        continue;
      }
      if (error && error.writeExpired) {
        row.status = 'warning';
        row.statusMessage = error.message;
        row.successIntent = row.currentOverrideIntent;
        warningCount += 1;
        m.redraw();
        continue;
      }
      var safelyNotInvoked = false;
      if (operation.activeWriteOperation) {
        try {
          safelyNotInvoked = await writeOperations.fail(
            operation.writeOperations,
            operation.activeWriteOperation.key,
            operation.activeWriteOperation.operation,
            error
          );
        } catch (recoveryError) {
          error = recoveryError;
        }
      }
      if ((error && error.batchRowExpired) ||
          (safelyNotInvoked && relinquishDeadlineExpired(operation))) {
        var expiryWarning = 'Scheduled relinquish deadline elapsed before this override row could finish; no new phase was sent.';
        try {
          await writeOperations.markBatchRowExpired(
            operation.writeOperations,
            'mass-override-batch',
            operation.batch,
            overrideRowKey(row),
            operation.usedWriteOperations || [],
            expiryWarning
          );
          row.status = 'warning';
          row.statusMessage = expiryWarning;
          row.successIntent = row.currentOverrideIntent;
          warningCount += 1;
        } catch (recoveryError) {
          row.status = 'error';
          row.statusMessage = errorMessage(recoveryError);
          failureCount += 1;
        }
      } else if (error && error.overrideApplied) {
        try {
          await writeOperations.markBatchRowSuccess(
            operation.writeOperations,
            'mass-override-batch',
            operation.batch,
            overrideRowKey(row),
            errorMessage(error)
          );
          row.status = 'warning';
          row.statusMessage = errorMessage(error);
          row.successIntent = row.currentOverrideIntent;
          warningCount += 1;
        } catch (recoveryError) {
          row.status = 'error';
          row.statusMessage = errorMessage(recoveryError);
          failureCount += 1;
        }
      } else {
        row.status = 'error';
        row.statusMessage = errorMessage(error) || 'Override failed';
        failureCount += 1;
      }
    }
    m.redraw();
  }

  if (saveToken !== component.saveToken) return false;
  component.saving = false;
  if (successCount) {
    component.successes = ['Successfully sent ' + successCount + ' override' + (successCount === 1 ? '' : 's') + '.'];
  }
  if (failureCount) {
    component.errors = [failureCount + ' override' + (failureCount === 1 ? '' : 's') + ' failed.'];
  }
  if (warningCount) {
    component.errors.push(warningCount + ' override' + (warningCount === 1 ? '' : 's') + ' applied with a warning.');
  }
  if ((successCount || warningCount) && component.submitCallback) {
    try {
      await component.submitCallback({
        successCount: successCount,
        failureCount: failureCount,
        warningCount: warningCount
      });
    } catch (callbackError) {
      console.error('Failed to refresh overrides after release:', callbackError);
    }
  }
  m.redraw();
};

component.view = function () {
  return m('div.mass-point-override-modal', [
    Model.errorView.call(component),
    Model.successView.call(component),
    modeControls(),
    valueControls(),
    relinquishControls(),
    component.loading
      ? m('div.f5.moon-gray.mb2', 'Loading point override configurations...')
      : null,
    m('table.w-100.f4.fw2.collapse', [
      m('thead', [
        m('tr', [
          m('th.bb.bw1.tc.v-btm.ph2', selectAllCheckbox()),
          m('th.bb.bw1.tl.v-btm.ph2', 'Point Path'),
          m('th.bb.bw1.tc.v-btm.ph2', 'Live Value'),
          m('th.bb.bw1.tc.v-btm.ph2', 'Current Mode'),
          m('th.bb.bw1.tc.v-btm.ph2', 'Override Action'),
          m('th.bb.bw1.tc.v-btm.ph2', 'Status')
        ])
      ]),
      m('tbody', component.rows.map(function (row) {
        var rowClass = row.disabled ? '.o-60' : '';
        var liveColumnRecord = row.columnRecord || {};
        return m('tr' + rowClass, { key: row.index }, [
          m('td.bb.tc.v-mid.ph2', rowCheckbox(row)),
          m('td.bb.tl.v-mid.ph2', {
            title: row.disabledReason || displayPath(row)
          }, [
            m('div.f5', displayPath(row)),
            row.disabledReason ? m('div.f6.moon-gray.mt1', row.disabledReason) : null
          ]),
          m(pointTd, {
            columnRecord: liveColumnRecord,
            parentRid: component.parentRid,
            options: {
              disableInteractions: true
            },
            tdClass: '.bb.bl-0',
            tdStyle: {
              cursor: 'default',
              pointerEvents: 'none'
            }
          }),
          m('td.bb.tc.v-mid.ph2.nowrap', displayOverrideState(row)),
          m('td.bb.tc.v-mid.ph2.nowrap', displayOverrideValue(row)),
          m('td.bb.tc.v-mid.ph2', statusCell(row))
        ]);
      }))
    ])
  ]);
};

component._test = {
  pointSupportsOverride: pointSupportsOverride,
  isDirectBacnetOverride: isDirectBacnetOverride,
  overrideSignature: overrideSignature,
  enabledRows: enabledRows,
  selectedRows: selectedRows,
  allEnabledRowsSelected: allEnabledRowsSelected,
  toggleAllRows: toggleAllRows,
  loadRows: loadRows,
  overrideValueForRow: overrideValueForRow,
  writeOverrideRow: writeOverrideRow,
  buildRelinquishPlan: buildRelinquishPlan,
  releaseAtForPlan: releaseAtForPlan,
  datetimeLocalFromTimestamp: datetimeLocalFromTimestamp
};

module.exports = component;
